import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
} from 'react';

import { localStorage } from 'shared/services/localStorage';

type SidebarCollapseContextType = {
  isOpen: boolean;
  setIsOpen: () => void;
};

const defaultSidebarCollapseContext: SidebarCollapseContextType = {
  isOpen: true,
  setIsOpen: () => {},
};

const SidebarCollapseContext = createContext(defaultSidebarCollapseContext);

const SIDEBAR_OPEN_STATE_STORAGE_KEY = 'sidebar-open-state';

export const SidebarCollapseProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Initialize state from localStorage, defaulting to false (collapsed)
  const [isOpen, setIsOpenState] = useState(() => {
    const savedState = localStorage.getItem<boolean>(
      SIDEBAR_OPEN_STATE_STORAGE_KEY
    );
    return savedState !== null ? savedState : false;
  });

  const setIsOpen = useCallback(() => {
    setIsOpenState((prevIsOpen: boolean) => {
      const newState = !prevIsOpen;
      // Persist state to localStorage
      localStorage.setItem(SIDEBAR_OPEN_STATE_STORAGE_KEY, newState);
      return newState;
    });
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
    }),
    [isOpen, setIsOpen]
  );

  return (
    <SidebarCollapseContext.Provider value={value}>
      {children}
    </SidebarCollapseContext.Provider>
  );
};

export const useSidebarCollapseContext = () => {
  const context = useContext(SidebarCollapseContext);

  if (!context) {
    throw new Error(
      'useSidebarCollapseContext must be used within a SidebarCollapseContext.Provider'
    );
  }

  return context;
};
